import React, { useState, useEffect, useRef } from 'react';
import { sendTestMessage, getChatHistory, getUserInfo, updateUser, getPatientGroups } from '../api/api';
import './TestChat.css';

const TestChat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    // Config / Persona State
    const [showConfig, setShowConfig] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [groups, setGroups] = useState([]);
    const [persona, setPersona] = useState({
        diagnosis: '',
        stage: '',
        biomarkers: '', // simplified as string for now
        group_id: '',
    });

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        loadHistory();
        loadConfigData();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const loadHistory = async () => {
        try {
            const history = await getChatHistory(100, 0);
            const msgs = history.map(entry => ({
                id: entry.timestamp || Math.random(),
                text: entry.content,
                sender: entry.role === 'user' ? 'user' : 'bot',
                timestamp: entry.timestamp,
            }));
            setMessages(msgs);
        } catch (err) {
            console.log('No history:', err.message);
        }
    };

    const loadConfigData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const [user, groupsData] = await Promise.all([
                    getUserInfo(token),
                    getPatientGroups()
                ]);
                setCurrentUser(user);
                setGroups(groupsData);

                // Init persona from user profile
                if (user.medical_profile) {
                    setPersona({
                        diagnosis: user.medical_profile.diagnosis || '',
                        stage: user.medical_profile.cancer_stage || '',
                        biomarkers: (user.medical_profile.biomarkers || []).join(', '),
                        group_id: user.group_id || '',
                    });
                }
            }
        } catch (err) {
            console.error('Error loading config data:', err);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || sending) return;

        const userMsg = { id: Date.now(), text, sender: 'user', timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setSending(true);
        setError('');

        try {
            const response = await sendTestMessage(text);
            const botMsg = {
                id: Date.now() + 1,
                text: response.reply,
                sender: 'bot',
                timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, botMsg]);
        } catch (err) {
            setError(err.message || 'Error enviando mensaje');
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const handleSavePersona = async () => {
        if (!currentUser) return;
        setLoadingConfig(true);
        try {
            // Updated user data structure based on typical schema
            const updateData = {
                group_id: persona.group_id || null, // Allow clearing group
                medical_profile: {
                    ...currentUser.medical_profile,
                    diagnosis: persona.diagnosis,
                    cancer_stage: persona.stage,
                    biomarkers: persona.biomarkers.split(',').map(b => b.trim()).filter(b => b),
                }
            };

            await updateUser(currentUser.email, updateData);
            alert('Perfil simulado actualizado. El chatbot ahora te verá con estos datos.');
            setShowConfig(false);

            // Optionally clear chat to start fresh
            // setMessages([]); 
        } catch (err) {
            alert('Error guardando perfil: ' + err.message);
        } finally {
            setLoadingConfig(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        if (window.confirm('¿Limpiar la conversación visual? (El historial en el servidor se mantiene)')) {
            setMessages([]);
        }
    };

    const formatTime = (ts) => {
        if (!ts) return '';
        try {
            return new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    return (
        <div className="test-chat-page">
            <div className="chat-container">
                {/* Header */}
                <div className="chat-header">
                    <div className="chat-header-info">
                        <div className="chat-avatar">V</div>
                        <div>
                            <h2>Viki Chat</h2>
                            <span className="chat-subtitle">Modo prueba — Admin</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => setShowConfig(true)} className="btn-clear" title="Configurar Persona">
                            ⚙️
                        </button>
                        <button onClick={handleClear} className="btn-clear" title="Limpiar chat">
                            🗑️
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="chat-messages">
                    {messages.length === 0 && (
                        <div className="chat-welcome">
                            <div className="welcome-icon">💬</div>
                            <p>Escribe un mensaje para probar el chatbot.</p>
                            <p className="welcome-hint">Usa el botón ⚙️ para cambiar tu perfil médico simulado (Diagnóstico, Grupo, etc.).</p>
                        </div>
                    )}

                    {messages.map((msg) => (
                        <div key={msg.id} className={`message ${msg.sender}`}>
                            <div className="message-bubble">
                                <p>{msg.text}</p>
                                <span className="message-time">{formatTime(msg.timestamp)}</span>
                            </div>
                        </div>
                    ))}

                    {sending && (
                        <div className="message bot">
                            <div className="message-bubble typing">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Error */}
                {error && <div className="chat-error">{error}</div>}

                {/* Input */}
                <div className="chat-input-bar">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Escribe un mensaje..."
                        rows={1}
                        disabled={sending}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || sending}
                        className="btn-send"
                    >
                        ➤
                    </button>
                </div>
            </div>

            {/* Config Panel Overlay */}
            {showConfig && (
                <div className="config-overlay" onClick={() => setShowConfig(false)}>
                    <div className="config-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="config-header">
                            <h3>Configurar Persona</h3>
                            <button className="btn-close-config" onClick={() => setShowConfig(false)}>×</button>
                        </div>

                        <div className="config-section">
                            <span className="config-title">Perfil Médico</span>

                            <div className="config-form-group">
                                <label>Diagnóstico (CIE-10 o texto)</label>
                                <select
                                    value={persona.diagnosis}
                                    onChange={(e) => setPersona({ ...persona, diagnosis: e.target.value })}
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="C50 - Neoplasia maligna de la mama">C50 - Ca Mama</option>
                                    <option value="C34 - Neoplasia maligna de los bronquios y del pulmón">C34 - Ca Pulmón</option>
                                    <option value="C61 - Neoplasia maligna de la próstata">C61 - Ca Próstata</option>
                                    <option value="Otro">Otro</option>
                                </select>
                            </div>

                            <div className="config-form-group">
                                <label>Etapa (Estadío)</label>
                                <select
                                    value={persona.stage}
                                    onChange={(e) => setPersona({ ...persona, stage: e.target.value })}
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="I">Etapa I</option>
                                    <option value="II">Etapa II</option>
                                    <option value="III">Etapa III</option>
                                    <option value="IV">Etapa IV</option>
                                </select>
                            </div>
                        </div>

                        <div className="config-section">
                            <span className="config-title">Contexto Viki</span>

                            <div className="config-form-group">
                                <label>Grupo de Pacientes</label>
                                <select
                                    value={persona.group_id}
                                    onChange={(e) => setPersona({ ...persona, group_id: e.target.value })}
                                >
                                    <option value="">Sin grupo</option>
                                    {groups.map(g => (
                                        <option key={g._id} value={g._id}>{g.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="config-section">
                            <p style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                                Nota: Al guardar, se actualizarán los datos de tu usuario actual ({currentUser?.email}) para simular la experiencia.
                            </p>
                        </div>

                        <button
                            className="btn-save-config"
                            onClick={handleSavePersona}
                            disabled={loadingConfig}
                        >
                            {loadingConfig ? 'Guardando...' : 'Guardar y Aplicar'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestChat;
